import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { groupBy, mapValues, upperFirst } from 'lodash';
import type { Document } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import type { Options } from '@contentful/rich-text-react-renderer';
import type { Segment, Locale } from '@fixter/i18n';
import { formatAmountWithCurrency, getLocale } from '@fixter/i18n';
import { extendCmsText } from '$cms/index';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import type { PriceDataRow } from '../schema';

/**
 * Content comes as `p` tag so we strip the tag to have the content of `description` and `descriptionAdditionalSku` flow inline.
 */
const descriptionRenderNode: Options['renderNode'] = { [BLOCKS.PARAGRAPH]: (text, children) => children };

const getLowestPrice = (pricesArray: PriceDataRow[]): number => {
  const minPrice = pricesArray.reduce(
    (lowest, { lowestPrice }) => (lowestPrice < lowest ? lowestPrice : lowest),
    Infinity
  );
  return Math.abs(minPrice / 100);
};

const getHighestPrice = (pricesArray: PriceDataRow[]): number => {
  const maxPrice = pricesArray.reduce(
    (highest, { highestPrice }) => (highestPrice > highest ? highestPrice : highest),
    0
  );
  return Math.abs(maxPrice / 100);
};

/**
 * Get an array of translated conjunctions (',' | 'and') that can be used to join array elements based on index.
 */
const getLocaleConjunctions = (locale: Locale, length: number): string[] => {
  const formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
  return formatter
    .formatToParts(Array(length).fill('.'))
    .flatMap(({ type, value }) => (type === 'literal' ? value : []));
};

/**
 * When there is only one unique SKU in `priceArray` render only `description`.
 * When more than one, render also `descriptionAdditionalSku`.
 */
export const renderDescription = (
  richTexts: [description: Document, descriptionAdditionalSku: Document | undefined],
  priceRows: PriceDataRow[],
  placeholders: Record<string, string | number>,
  segment: Segment
): ReactNode => {
  const [description, descriptionAdditionalSku] = richTexts;

  /**
   * When no sku exists, eg. on /make/model pages we don't need to compile a price range
   * or an additional description so we don't send the priceArray data
   */
  if (!priceRows.length) {
    const options = {
      renderNode: descriptionRenderNode,
    };
    const noRangeDescription: ReactNode = renderRichTextContent(description, {
      ...options,
      placeholder: placeholders,
    });
    return noRangeDescription;
  }

  const skuPricesGroup: {
    [sku: string]: {
      readonly skuName: string;
      readonly lowestPrice: string; // lowest corresponding to the sku
      readonly highestPrice: string; // highest corresponding to the sku
    };
  } = mapValues(groupBy(priceRows, 'sku'), (value) => ({
    skuName: value[0].skuName,
    lowestPrice: formatAmountWithCurrency(getLowestPrice(value), segment, 0),
    highestPrice: formatAmountWithCurrency(getHighestPrice(value), segment, 0),
  }));

  const locale = getLocale(segment);
  const conjunctions = getLocaleConjunctions(locale, Object.keys(skuPricesGroup).length);
  conjunctions[0] = ' '; // start with space

  return Object.entries(skuPricesGroup).map(
    ([sku, { skuName, lowestPrice, highestPrice }], i, { length }) => {
      let skuDescription: ReactNode = '';
      const options = {
        renderNode: descriptionRenderNode,
      };
      const placeholder = {
        ...placeholders,
        skuName,
        lowestPrice,
        highestPrice,
      };
      if (i === 0) {
        skuDescription = renderRichTextContent(description, {
          ...options,
          placeholder,
        });
      } else if (descriptionAdditionalSku) {
        const extendedDescriptionAdditionalSku = structuredClone(descriptionAdditionalSku);
        const firstTextBlock = extendedDescriptionAdditionalSku.content[0].content[0];
        if (firstTextBlock.nodeType === 'text') {
          firstTextBlock.value = extendCmsText(firstTextBlock.value, placeholder);
          if (i === 1) firstTextBlock.value = upperFirst(firstTextBlock.value);
        }
        skuDescription = (
          <>
            {conjunctions[i - 1]}
            {renderRichTextContent(extendedDescriptionAdditionalSku, options)}
            {i === length - 1 && '.'}
          </>
        );
      }
      return <Fragment key={sku}>{skuDescription}</Fragment>;
    }
  );
};
